
import { BlogPost } from "@/types/blogTypes";
import { extractExcerpt } from "@/utils/blogUtils";
import { getCanonicalUrl } from "@/utils/seoUtils";
import { repairDisplayText } from "@/utils/displayText";
import { getAuthorProfile } from "@/data/authors";

export interface BlogSeoProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  noIndex?: boolean;
  blogPost?: BlogPost;
  schemaType?: "BlogPosting" | "WebSite" | "Organization";
  breadcrumbs?: Array<{
    name: string;
    url: string;
  }>;
  schema?: Record<string, unknown>; // Add schema support
}

/**
 * Hook to generate SEO props for blog content
 */
export const getBlogSeoProps = (
  post?: BlogPost | null,
  slug?: string,
  defaultProps: Partial<BlogSeoProps> = {}
): BlogSeoProps => {
  // If no post is provided, return default or loading SEO props
  if (!post) {
    return {
      title: defaultProps.title || (slug ? "Post Not Found" : "Blog"),
      description:
        defaultProps.description ||
        (slug
          ? "The blog post you're looking for could not be found."
          : "Elf Letter Bot blog posts and articles"),
      noIndex: defaultProps.noIndex ?? Boolean(slug),
      ogType: defaultProps.ogType || "website",
      schemaType: defaultProps.schemaType || "WebSite",
      ...defaultProps
    };
  }

  // Extract SEO props from the post
  const seoDescription = repairDisplayText(
    post.metadata?.seo_description || 
    post.excerpt || 
    extractExcerpt(post.content));
    
  // Generate a more optimized URL if needed
  const canonicalUrl = slug 
    ? getCanonicalUrl(`/blog/${slug}`) 
    : undefined;

  const postUrl = canonicalUrl || getCanonicalUrl(`/blog/${post.slug}`);
  const author = getAuthorProfile(post.metadata?.author);
  const wordCount = post.content ? post.content.replace(/<[^>]*>/g, '').split(/\s+/).filter(Boolean).length : undefined;

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "headline": repairDisplayText(post.title),
        "description": seoDescription,
        "image": post.featured_image || "https://elfletterbot.com/opengraph-image.png",
        "author": {
          "@type": author.kind === "editorial" ? "Organization" : "Person",
          "name": author.displayName,
          ...(author.href ? { url: `https://elfletterbot.com${author.href}` } : {})
        },
        "publisher": {
          "@type": "Organization",
          "name": "Elf Letter Bot",
          "logo": {
            "@type": "ImageObject",
            "url": "https://elfletterbot.com/logo.png"
          }
        },
        "datePublished": post.created_at,
        "dateModified": post.updated_at,
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": postUrl
        },
        ...(wordCount ? { "wordCount": wordCount } : {}),
        "articleSection": "Christmas & Holiday Ideas",
        "inLanguage": "en-US"
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://elfletterbot.com/" },
          { "@type": "ListItem", "position": 2, "name": "Blog", "item": getCanonicalUrl("/blog") },
          { "@type": "ListItem", "position": 3, "name": repairDisplayText(post.title) }
        ]
      }
    ]
  };

  return {
    title: repairDisplayText(post.title),
    description: seoDescription,
    canonicalUrl: canonicalUrl,
    ogImage: post.featured_image || undefined,
    ogType: "article",
    noIndex: !post.published,
    blogPost: post,
    schemaType: "BlogPosting",
    schema: schema
  };
};

export const useBlogSeo = (
  post?: BlogPost | null,
  slug?: string,
  defaultProps: Partial<BlogSeoProps> = {}
): BlogSeoProps => {
  return getBlogSeoProps(post, slug, defaultProps);
};
